import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { collection, collectionData, CollectionReference, doc, docData, DocumentData, Firestore, setDoc, updateDoc, deleteDoc, serverTimestamp, addDoc } from '@angular/fire/firestore';
import { getDownloadURL, ref, Storage, uploadString } from '@angular/fire/storage';
import { Photo } from '@capacitor/camera';
import { geohashForLocation } from 'geofire-common';
import { Observable, Subscription } from 'rxjs';
import { Card } from '../interfaces/card';
import { Drivers } from '../interfaces/drivers';
import { Rider } from '../interfaces/rider';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  private driverCollection: CollectionReference<DocumentData>;
  directory: any;
  userUID: string;
  userName: string;
  userEmail: string;
  profile: DocumentData;
  pathM: string;
  private authStateSubscription: () => void;

  constructor(
    private auth: Auth,
    public firestore: Firestore,
    private storage: Storage,
    private authService: AuthService
  ) {
    this.authStateSubscription = onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.initializeUser(user);
      } else {
        this.userName = "None";
      }
    });
  }

  private initializeUser(user: User) {
    this.driverCollection = collection(this.firestore, 'Drivers');
    this.getUserProfile(user).subscribe(data => {
      this.profile = data;
      if (this.profile) {
        this.userName = this.profile.Driver_name || "None";
        this.pathM = `uploads/${this.profile.uid}/profile.png`;
      }
    });
  }

  getUserProfile(user: User): Observable<DocumentData> {
    const userDocRef = doc(this.firestore, `Drivers/${user.uid}`);
    return docData(userDocRef);
  }

  async requestRideWithRiderDetails(user_Loc_coord: any, user_Des_coord: any, ID: string): Promise<void> {
    try {
      const loc: Rider = {
        Loc_lat: user_Loc_coord.lat,
        Loc_lng: user_Loc_coord.lng,
        Rider_id: 'syusyugdshgsghdsdssd',
        Rider_name: "Chinedu",
        Rider_phone: 6478947352,
        Rider_imgUrl: "https://avatars.githubusercontent.com/u/7928001?v=4",
        Rider_rating: 3,
        Des_lat: user_Des_coord.lat,
        Des_lng: user_Des_coord.lng,
        Rider_Location: 'Owerri Imo State',
        Rider_Destination: 'Kenville Hotels Amabar',
        Rider_email: 'chndht@gmiail.ccjd',
        countDown: 10,
        cancel: false,
        price: 2230,
        cash: true,
        time: ''
      };

      const userDocRef = doc(this.firestore, 'Drivers', ID);
      await updateDoc(userDocRef, { onlineState: false });
      await setDoc(doc(this.firestore, "Request", ID), { ...loc });
    } catch (e) {
      console.error('Error requesting ride:', e);
      throw new Error('Error requesting ride');
    }
  }

  async rejectRider(): Promise<boolean> {
    try {
      const userDocRef = doc(this.firestore, "Request", this.auth.currentUser.uid);
      await updateDoc(userDocRef, {
        cancel: true,
        start: false
      });
      await this.updateOnlineState(true);
      return true;
    } catch (e) {
      console.error('Error rejecting rider:', e);
      return false;
    }
  }

  async pickRider(): Promise<boolean> {
    try {
      const userDocRef = doc(this.firestore, "Request", this.auth.currentUser.uid);
      await updateDoc(userDocRef, {
        start: true,
      });
      return true;
    } catch (e) {
      console.error('Error picking rider:', e);
      return false;
    }
  }

  async endRide(): Promise<boolean> {
    try {
      const userDocRef = doc(this.firestore, "Request", this.auth.currentUser.uid);
      await updateDoc(userDocRef, {
        stop: true,
        start: false
      });
      return true;
    } catch (e) {
      console.error('Error ending ride:', e);
      return false;
    }
  }

  async createHistory(user: Rider): Promise<void> {
    try {
      const loc: Rider = {
        ...user,
        time: serverTimestamp(),
      };
      await setDoc(doc(this.firestore, `Drivers/${this.auth.currentUser.uid}/History/${user.Rider_id}`), { ...loc });
      await setDoc(doc(this.firestore, `AllRides/${this.auth.currentUser.uid}/customer/${user.Rider_id}`), { ...loc });
    } catch (e) {
      console.error('Error creating history:', e);
      throw new Error('Error creating history');
    }
  }

  async goOffline(): Promise<void> {
    try {
      await deleteDoc(doc(this.firestore, "Drivers", this.auth.currentUser.uid));
    } catch (e) {
      console.error('Error going offline:', e);
      throw new Error('Error going offline');
    }
  }

  async deleteDriverFromRequest(ID: string): Promise<void> {
    try {
      await deleteDoc(doc(this.firestore, "Request", ID));
    } catch (e) {
      console.error('Error deleting driver from request:', e);
      throw new Error('Error deleting driver from request');
    }
  }

  async updateCountDown(time: number): Promise<boolean> {
    try {
      const userDocRef = doc(this.firestore, "Request", this.auth.currentUser.uid);
      await updateDoc(userDocRef, {
        countDown: time,
      });
      return true;
    } catch (e) {
      console.error('Error updating countdown:', e);
      return false;
    }
  }

  async pushDriverToRequest(coord: any, name: string, email: string, phone: any, car: string, cartype: string, plate: string, imageUrl: string, document: string): Promise<void> {
    try {
      const loc: Drivers = {
        geohash: geohashForLocation([coord.coords.latitude, coord.coords.longitude]),
        Driver_lat: coord.coords.latitude,
        Driver_lng: coord.coords.longitude,
        Driver_id: this.auth.currentUser.uid,
        Driver_name: name,
        Driver_car: car,
        Driver_imgUrl: imageUrl,
        Driver_rating: 0,
        distance: 0,
        duration: 0,
        seats: 1,
        start: false,
        stop: false,
        intransit: false,
        cancel: false,
        Driver_cartype: cartype,
        Driver_plate: plate,
        Driver_num_rides: 0,
        Document: document,
        Driver_email: email,
        Driver_phone: phone,
        onlineState: false,
        Earnings: 0
      };
      await updateDoc(doc(this.firestore, "Request", this.auth.currentUser.uid), { ...loc });
    } catch (e) {
      console.error('Error pushing driver to request:', e);
      throw new Error('Error pushing driver to request');
    }
  }

  async createNewDriver(coord: any, name: string, email: string, phone: any, car: string, cartype: string, plate: string, imageUrl: string, document: string): Promise<void> {
    try {
      const loc: Drivers = {
        geohash: geohashForLocation([coord.coords.latitude, coord.coords.longitude]),
        Driver_lat: coord.coords.latitude,
        Driver_lng: coord.coords.longitude,
        Driver_id: this.auth.currentUser.uid,
        Driver_name: name,
        Driver_car: car,
        Driver_imgUrl: imageUrl,
        Driver_rating: 0,
        distance: 0,
        duration: 0,
        seats: 1,
        start: false,
        stop: false,
        intransit: false,
        cancel: false,
        Driver_cartype: cartype,
        Driver_plate: plate,
        Driver_num_rides: 0,
        Document: document,
        Driver_email: email,
        Driver_phone: phone,
        onlineState: false,
        Earnings: 0
      };
      await setDoc(doc(this.firestore, "Drivers", this.auth.currentUser.uid), { ...loc });
    } catch (e) {
      console.error('Error creating new driver:', e);
      throw new Error('Error creating new driver');
    }
  }

  getDriver(): Observable<Drivers[]> {
    return collectionData(this.driverCollection, {
      idField: 'id',
    }) as Observable<Drivers[]>;
  }

  updateDriver(driver: Drivers): Promise<void> {
    const driverDocRef = doc(this.firestore, `Drivers/${driver.Driver_id}`);
    return updateDoc(driverDocRef, { ...driver });
  }

  async uploadImage(cameraFile: Photo, uid: string): Promise<boolean> {
    const storageRef = ref(this.storage, this.pathM);
    try {
      await uploadString(storageRef, cameraFile.base64String, 'base64');
      const imageUrl = await getDownloadURL(storageRef);
      const userDocRef = doc(this.firestore, `Drivers/${uid}`);
      await updateDoc(userDocRef, { imageUrl });
      return true;
    } catch (e) {
      console.error('Error uploading image:', e);
      return false;
    }
  }

  getMessage(): Observable<DocumentData[]> {
    const userDocRef = collection(this.firestore, `Messages/${this.auth.currentUser.uid}/messages`);
    return collectionData(userDocRef);
  }

  getCards(): Observable<DocumentData[]> {
    const userDocRef = collection(this.firestore, `Drivers/${this.auth.currentUser.uid}/Cards`);
    return collectionData(userDocRef);
  }

  getEarnings(): Observable<DocumentData> {
    const userDocRef = doc(this.firestore, `Drivers/${this.auth.currentUser.uid}`);
    return docData(userDocRef);
  }

  getCartypes(): Observable<DocumentData[]> {
    const cartypesRef = collection(this.firestore, `Cartypes`);
    return collectionData(cartypesRef);
  }

  getRequests(): Observable<DocumentData> {
    const requestsRef = doc(this.firestore, `Request/${this.auth.currentUser.uid}`);
    return docData(requestsRef);
  }

  getDrivers(): Observable<DocumentData[]> {
    const driversRef = collection(this.firestore, `Drivers`);
    return collectionData(driversRef);
  }

  async addChatMessage(msg: string): Promise<void> {
    try {
      await addDoc(collection(this.firestore, `Messages/${this.profile.Driver_id}/messages`), {
        msg,
        from: this.auth.currentUser.uid,
        createdAt: serverTimestamp(),
        myMsg: true,
        fromName: this.profile.Driver_name
      });
    } catch (e) {
      console.error('Error adding chat message:', e);
      throw new Error('Error adding chat message');
    }
  }

  async createCard(name: string, number: any, type: string, id: string): Promise<boolean> {
    try {
      const card: Card = {
        name,
        number,
        type,
        id,
        selected: true
      };
      await setDoc(doc(this.firestore, `Drivers/${this.auth.currentUser.uid}/Cards/${name}`), { ...card });
      return true;
    } catch (e) {
      console.error('Error creating card:', e);
      return false;
    }
  }

  async updateCard(name: string, number: any, type: string, id: string, state: boolean): Promise<boolean> {
    try {
      const card: Card = {
        name,
        number,
        type,
        id,
        selected: state
      };
      await updateDoc(doc(this.firestore, `Drivers/${this.profile.Rider_id}/Cards/${name}`), { ...card });
      return true;
    } catch (e) {
      console.error('Error updating card:', e);
      return false;
    }
  }

  async updateOnlineState(state: boolean): Promise<boolean> {
    try {
      const userDocRef = doc(this.firestore, `Drivers/${this.auth.currentUser.uid}`);
      await updateDoc(userDocRef, { onlineState: state });
      return true;
    } catch (e) {
      console.error('Error updating online state:', e);
      return false;
    }
  }

  async updateEarnings(value: number): Promise<boolean> {
    try {
      const userDocRef = doc(this.firestore, `Drivers/${this.auth.currentUser.uid}`);
      await updateDoc(userDocRef, { Earnings: value });
      return true;
    } catch (e) {
      console.error('Error updating earnings:', e);
      return false;
    }
  }

  ngOnDestroy(): void {
    if (this.authStateSubscription) {
      this.authStateSubscription();
    }
  }
}
